import { useQuery } from '@tanstack/react-query';

import type {
  DashboardRateInput,
  RangeInput,
  VolumeIncidentsEvolutionBackOutput,
  VolumeIncidentsEvolutionOutput,
  VolumeIncidentsEvolutionPoint,
  VolumeIncidentsInterval,
} from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toRateInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

/**
 * Le back livre `VolumeIncidentsEvolutionBackOutput` (shape `{ items: [...] }`
 * issu du package @allocab/allocab-extranet-api-types). On reshape vers
 * `VolumeIncidentsEvolutionOutput` (`{ points, granularity }`) consommé par
 * `TimeSeriesChart` :
 *   - `date` timestamp → ISO 8601
 *   - `nbTrips` → `rides`
 *   - `incidentRate` ratio 0..1 → % (×100)
 *   - injection de `granularity` depuis l'input (back ne le remonte pas)
 */
const toIsoDate = (date: number | string): string => (typeof date === 'number' ? new Date(date).toISOString() : date);

export const useIncidentTimeSeries = (input: DashboardRateInput, interval: VolumeIncidentsInterval = 'day') =>
  useQuery({
    queryKey: extranetQueryKeys.dashboard.incidentTimeSeries({ ...input, interval }),
    queryFn: () =>
      getExtranetApi().get<RangeInput & { interval: VolumeIncidentsInterval }, VolumeIncidentsEvolutionBackOutput>(
        '/v1/performance/volume-incidents-evolution',
        { ...toRateInput(input), interval },
      ),
    select: (data): VolumeIncidentsEvolutionOutput => ({
      points: data.items.map((p): VolumeIncidentsEvolutionPoint => ({
        date: toIsoDate(p.date),
        rides: p.nbTrips,
        incidentRate: p.incidentRate * 100,
      })),
      granularity: interval,
    }),
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });
